import { useState, useEffect, useCallback, useContext, lazy, Suspense } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getServiceForEntity } from '@/services/serviceRegistry';
import { useClearWhenFilterExcludes, useLookupByKey, useLookupOptions, type LookupFilterValues } from '@/lib/lookupOptions';
import { recentGroupProps } from '@/lib/lookupRecentGroup';
import { entityDetailPath, canonicalModuleName } from '@/lib/pageEntityContext';
import { useModifierKeyHeld } from '@/hooks/useModifierKeyHeld';
import { useHasPermission } from '@/components/PermissionGate';
import { InsideReferencePeekContext } from '@/components/InsideReferencePeekContext';
import { FieldLabel } from './FieldLabel';
import { REF_PEEK_CLASS, revealLookupControlsSx } from '@/components/referencePeekReveal';

/** Lazy: the peek panel pulls in JsonForms + all renderers; only pay on first use. */
const ReferencePeekPanelLazy = lazy(() => import('@/components/ReferencePeekPanel'));

export interface EntityLookupOption {
  id: string;
  [key: string]: any;
}

export interface EntityLookupProps {
  entity: string;              // e.g., "Customer"
  module: string;              // e.g., "sales"
  value: string | null;        // UUID or code string (when valueField is set)
  onChange: (value: string | null) => void;
  onEntitySelect?: (entity: EntityLookupOption | null) => void;  // Callback with full entity data for autoFill
  displayField: string;        // e.g., "name"
  displayPrefixField?: string; // Prepended to displayField in the shown label, e.g. "accountNumber" → "4056 – Purchases"
  additionalFields?: string[]; // e.g., ["email", "phone"]
  label?: string;
  description?: string;        // Shown as tooltip on hover over label
  required?: boolean;
  disabled?: boolean;
  error?: string;
  minSearchLength?: number;    // Deprecated/ignored: pickers always show a default page and search from the first character
  onOpenModal?: () => void;    // Callback to open search modal
  valueField?: string;         // Field to use as stored value (default: "id"). For codeLookup, e.g., "code"
  filters?: LookupFilterValues; // Dependent-lookup filter values (lookup spec §5)
  filterSourceMissing?: boolean; // A required filter's source is empty: the value is cleared (lookup spec §5.4)
  hint?: string;               // Helper text when there is no error (e.g. "Select X first")
  recentlyUsedSlot?: string;   // The field's recently-used slot (x-lookup); absent = no "Recently used" group (lookup spec §4)
}

/**
 * The option the picker shows for `value`. The value can change from outside the picker — a copy
 * that overwrites it, an undo of a copy — so a selection that no longer matches it is dropped
 * (`onDropped` clears what goes with it) and the new value resolves in the picker. Adjusted while
 * rendering, so the stale label is never painted.
 */
function useSelectedOption(value: string | null, valueField: string, onDropped: () => void) {
  const [selectedOption, setSelectedOption] = useState<EntityLookupOption | null>(null);
  const [valueSeen, setValueSeen] = useState(value);
  if (value !== valueSeen) {
    setValueSeen(value);
    if (selectedOption && selectedOption[valueField] !== value) {
      setSelectedOption(null);
      onDropped();
    }
  }
  return [selectedOption, setSelectedOption] as const;
}

export function EntityLookup({
  entity,
  module,
  value,
  onChange,
  onEntitySelect,
  displayField,
  displayPrefixField,
  additionalFields = [],
  label,
  description,
  required = false,
  disabled = false,
  error,
  onOpenModal,
  valueField = 'id',
  filters,
  filterSourceMissing = false,
  hint,
  recentlyUsedSlot,
}: EntityLookupProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useSelectedOption(value, valueField, () => setInputValue(''));
  const [peekOpen, setPeekOpen] = useState(false);
  // Stays true after the first open so the drawer remains mounted and its
  // slide-out animation can run on close.
  const [peekMounted, setPeekMounted] = useState(false);
  const modifierHeld = useModifierKeyHeld();

  // Opening the referenced record — peek panel or full view — is offered only
  // when the user may read that entity; the server enforces this regardless.
  // Suppressed inside the peek panel itself: no nested peeks.
  const insidePeekPanel = useContext(InsideReferencePeekContext);
  const canReadReference =
    useHasPermission(`${canonicalModuleName(module)}.${entity}.Read`) && !insidePeekPanel;

  // The referenced record's id: the stored value for id-valued lookups; for
  // codeLookup (valueField "code") it only exists once the record is resolved.
  const referencedId = valueField === 'id' ? value : (selectedOption?.id ?? null);
  const referencePath =
    canReadReference && referencedId ? entityDetailPath(module, entity, referencedId) : null;

  // The shown label: "prefix – display" when a prefix field is configured (e.g. account number
  // before the account name), otherwise just the display field.
  const labelFor = useCallback((option: EntityLookupOption | null): string => {
    if (!option) return '';
    const display = option[displayField] || '';
    const prefix = displayPrefixField ? option[displayPrefixField] : undefined;
    return prefix ? `${prefix} – ${display}` : display;
  }, [displayField, displayPrefixField]);

  // Resolve the stored value once for display (cached, so a remount does not refetch). A code
  // value resolves by an exact key read; an id loads the full record, whose fields the label and
  // the peek panel may need beyond the lookup DTO.
  const resolvesByKey = valueField !== 'id';
  const { data: resolvedByKey, isLoading: isResolvingKey } = useLookupByKey(
    module, entity, value, valueField, resolvesByKey && !selectedOption,
  );
  const { data: resolvedById, isLoading: isResolvingId } = useQuery<EntityLookupOption | null>({
    queryKey: ['entityLookup', module, entity, value, valueField],
    queryFn: async () => {
      const service = await getServiceForEntity(module, entity);
      return service.getById(value!);
    },
    enabled: !resolvesByKey && !!value && !selectedOption,
  });
  const resolvedEntity = resolvesByKey ? resolvedByKey : resolvedById;
  const isResolvingValue = isResolvingKey || isResolvingId;

  // Sync the resolved value into local state — only the current value's: a read for one the
  // form has since moved away from is not shown.
  useEffect(() => {
    if (resolvedEntity && !selectedOption && resolvedEntity[valueField] === value) {
      setSelectedOption(resolvedEntity);
      setInputValue(labelFor(resolvedEntity));
    }
  }, [resolvedEntity, selectedOption, value, valueField, labelFor]);

  // No minimum length: an empty query returns the first page (25), so a default list shows on open
  // with no typing; typing narrows from the first character.
  // With a slot, an empty query opens on the field's recently used values (valid ones only), then the
  // first page; a typed query floats them to the top of the results.
  const { options, recentCount, loading: searchLoading, recordRecent } = useLookupOptions({
    module, entity, query: inputValue, pageSize: 25, filters, enabled: open,
    recentSlot: recentlyUsedSlot, valueField,
  });

  const clearedByFilter = useClearWhenFilterExcludes({
    module, entity, value, valueField, filters, sourceMissing: filterSourceMissing,
    onCleared: () => {
      setSelectedOption(null);
      setInputValue('');
      onChange(null);
    },
  });

  // Format option display
  const formatOptionLabel = (option: EntityLookupOption) => {
    const primary = labelFor(option);
    const additional = additionalFields
      .map(field => option[field])
      .filter(Boolean)
      .join(' • ');
    
    return (
      <Box>
        <Typography variant="body2">{primary}</Typography>
        {additional && (
          <Typography variant="caption" color="text.secondary">
            {additional}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Autocomplete
        fullWidth
        sx={revealLookupControlsSx(peekOpen)}
        value={selectedOption}
        onChange={(_, newValue) => {
          setSelectedOption(newValue);
          onChange(newValue ? newValue[valueField] : null);
          if (newValue) recordRecent(newValue);
          clearedByFilter.dismiss();
          // Call onEntitySelect with full entity data for autoFill functionality
          if (onEntitySelect) {
            onEntitySelect(newValue);
          }
        }}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => {
          setInputValue(newInputValue);
        }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        openOnFocus
        options={options}
        {...recentGroupProps(options, recentCount, t('lookup.recentlyUsed', { defaultValue: 'Recently used' }))}
        getOptionLabel={(option) => labelFor(option)}
        isOptionEqualToValue={(option, value) => option[valueField] === value[valueField]}
        filterOptions={(x) => x}  // Disable built-in filtering - we filter ourselves
        loading={isResolvingValue || searchLoading}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={<FieldLabel label={label || entity} description={description} />}
            required={required}
            error={!!error}
            // Right after a filter cleared the value, say why — more useful than the "required"
            // error the now-empty field raises (which still marks the field).
            helperText={(clearedByFilter.cleared
              ? t('lookup.clearedByFilter', { defaultValue: 'Cleared: not valid for the new selection' })
              : error || hint) || ''}
            // Cmd/Ctrl+click on the field opens the referenced record in a new
            // tab (browser convention). Capture phase + preventDefault on
            // mousedown keeps the click from focusing the input and opening
            // the dropdown; the click-capture suppresses the leftover click.
            onMouseDownCapture={(e) => {
              if ((e.metaKey || e.ctrlKey) && referencePath) {
                e.preventDefault();
                e.stopPropagation();
                window.open(referencePath, '_blank');
              }
            }}
            onClickCapture={(e) => {
              if ((e.metaKey || e.ctrlKey) && referencePath) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            sx={
              modifierHeld && referencePath
                ? {
                    '& .MuiAutocomplete-input': {
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }
                : undefined
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {referencePath && (
                    <IconButton
                      className={REF_PEEK_CLASS}
                      size="small"
                      title={t('lookup.quickView', { defaultValue: 'Quick view' })}
                      aria-label={t('lookup.quickView', { defaultValue: 'Quick view' })}
                      onMouseDown={(e) => {
                        // Keep the click from focusing the input / opening the dropdown.
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (e.metaKey || e.ctrlKey) {
                          window.open(referencePath, '_blank');
                          return;
                        }
                        setPeekMounted(true);
                        setPeekOpen(true);
                      }}
                      sx={{ p: 0.25 }}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                  {(isResolvingValue || searchLoading) ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option[valueField]}>
            {formatOptionLabel(option)}
          </li>
        )}
        noOptionsText={`No results found`}
      />
      
      {onOpenModal && (
        <IconButton
          onClick={onOpenModal}
          disabled={disabled}
          sx={{ mt: 1 }}
          title="Open search dialog"
        >
          <SearchIcon />
        </IconButton>
      )}

      {peekMounted && referencedId && (
        <Suspense fallback={null}>
          <ReferencePeekPanelLazy
            open={peekOpen}
            onClose={() => setPeekOpen(false)}
            module={module}
            entity={entity}
            entityId={referencedId}
            recordLabel={labelFor(selectedOption)}
          />
        </Suspense>
      )}
    </Box>
  );
}
